import { useMemo, useState } from 'react';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { isAxiosError } from 'axios';
import {
  formatLeaderboardDateParam,
  getLeaderboardDisplayName,
  useFulfillmentModelLeaderboard,
} from 'dashboard409a/services/fulfillmentModelLeaderboard';
import type { FulfillmentModelLeaderboardEntry } from 'dashboard409a/services/fulfillmentModelLeaderboard.types';

interface FulfillmentModelsLeaderboardModalProps {
  open: boolean;
  onClose: () => void;
}

const getLeaderboardSortValue = (
  entry: FulfillmentModelLeaderboardEntry,
  showPreparer: boolean,
  showReviewer: boolean
): number => {
  if (showPreparer && showReviewer) {
    return entry.total_model_count;
  }
  if (showPreparer) {
    return entry.preparer_model_count;
  }
  if (showReviewer) {
    return entry.reviewer_model_count;
  }
  return entry.total_model_count;
};

export const FulfillmentModelsLeaderboardModal = ({
  open,
  onClose,
}: FulfillmentModelsLeaderboardModalProps) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [showPreparer, setShowPreparer] = useState(true);
  const [showReviewer, setShowReviewer] = useState(true);

  const dateFilters = useMemo(
    () => ({
      fromDate: formatLeaderboardDateParam(fromDate),
      toDate: formatLeaderboardDateParam(toDate),
    }),
    [fromDate, toDate]
  );

  const { data, isLoading, isFetching, error, refetch } = useFulfillmentModelLeaderboard(
    open,
    dateFilters
  );

  const results = useMemo(() => data?.results ?? [], [data]);

  const sortedResults = useMemo(() => {
    const rows = [...results];
    rows.sort((left, right) => {
      const countDiff =
        getLeaderboardSortValue(right, showPreparer, showReviewer) -
        getLeaderboardSortValue(left, showPreparer, showReviewer);
      if (countDiff !== 0) {
        return countDiff;
      }
      const leftName = getLeaderboardDisplayName(left).toLowerCase();
      const rightName = getLeaderboardDisplayName(right).toLowerCase();
      return leftName.localeCompare(rightName);
    });
    return rows;
  }, [results, showPreparer, showReviewer]);

  const isForbidden = useMemo(
    () => isAxiosError(error) && error.response?.status === 403,
    [error]
  );

  const isInvalidDateRange = useMemo(
    () => isAxiosError(error) && error.response?.status === 400,
    [error]
  );

  const hasDateFilters = fromDate !== null || toDate !== null;

  const handleClearFilters = () => {
    setFromDate(null);
    setToDate(null);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="fulfillment-models-leaderboard-title"
    >
      <DialogTitle id="fulfillment-models-leaderboard-title" sx={{ py: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <EmojiEventsOutlinedIcon fontSize="small" aria-hidden />
            <Typography variant="body1" fontWeight="medium" component="span">
              Fulfillment models leaderboard
            </Typography>
          </Box>
          <Tooltip title="Refresh leaderboard">
            <span>
              <IconButton
                size="small"
                aria-label="Refresh fulfillment models leaderboard"
                data-testid="fulfillment-models-leaderboard-refresh"
                disabled={isLoading || isFetching}
                onClick={() => refetch()}
              >
                {isFetching ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers data-testid="fulfillment-models-leaderboard-content">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Distinct fulfillment models per analyst by supporting due date. Each model is counted once
          per person even if they hold both roles. All-time results only include analysts with at
          least 50 models; date-filtered results include everyone with at least one matching model.
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          sx={{ mb: 2 }}
          data-testid="fulfillment-models-leaderboard-filters"
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">From</Typography>
            <DatePicker
              value={fromDate}
              onChange={value => setFromDate(value)}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  inputProps: { 'data-testid': 'fulfillment-models-leaderboard-from-date' },
                  sx: {
                    width: 140,
                    '& .MuiInputBase-input': { py: '5px', fontSize: '0.8125rem' },
                  },
                },
                openPickerButton: { size: 'small', sx: { p: '4px' } },
              }}
            />
            <Typography variant="body2">To</Typography>
            <DatePicker
              value={toDate}
              onChange={value => setToDate(value)}
              minDate={fromDate ?? undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  inputProps: { 'data-testid': 'fulfillment-models-leaderboard-to-date' },
                  sx: {
                    width: 140,
                    '& .MuiInputBase-input': { py: '5px', fontSize: '0.8125rem' },
                  },
                },
                openPickerButton: { size: 'small', sx: { p: '4px' } },
              }}
            />
          </Stack>

          <ToggleButtonGroup
            size="small"
            value={[...(showPreparer ? ['preparer'] : []), ...(showReviewer ? ['reviewer'] : [])]}
            onChange={(_, values: string[]) => {
              setShowPreparer(values.includes('preparer'));
              setShowReviewer(values.includes('reviewer'));
            }}
            aria-label="Leaderboard role columns"
            data-testid="fulfillment-models-leaderboard-role-toggle"
          >
            <ToggleButton value="preparer" sx={{ py: '3px', fontSize: '0.75rem' }}>
              Preparer
            </ToggleButton>
            <ToggleButton value="reviewer" sx={{ py: '3px', fontSize: '0.75rem' }}>
              Reviewer
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            disabled={!hasDateFilters}
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: '3px', px: '10px' }}
            data-testid="fulfillment-models-leaderboard-clear-filters"
          >
            Clear dates
          </Button>
        </Stack>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4} role="status" aria-live="polite">
            <CircularProgress size={32} aria-label="Loading leaderboard" />
          </Box>
        )}

        {!isLoading && isForbidden && (
          <Alert severity="warning" data-testid="fulfillment-models-leaderboard-forbidden">
            You do not have permission to view the fulfillment models leaderboard.
          </Alert>
        )}

        {!isLoading && !isForbidden && isInvalidDateRange && (
          <Alert severity="error" data-testid="fulfillment-models-leaderboard-invalid-dates">
            Invalid date range. From date must be on or before to date.
          </Alert>
        )}

        {!isLoading && !isForbidden && !isInvalidDateRange && error && (
          <Alert severity="error" data-testid="fulfillment-models-leaderboard-error">
            Failed to load the leaderboard. Try refreshing.
          </Alert>
        )}

        {!isLoading && !isForbidden && !isInvalidDateRange && !error && sortedResults.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 3, textAlign: 'center' }}
            data-testid="fulfillment-models-leaderboard-empty"
          >
            {hasDateFilters
              ? 'No fulfillment model assignments match this date range.'
              : 'No fulfillment model assignments to show yet.'}
          </Typography>
        )}

        {!isLoading && !isForbidden && !isInvalidDateRange && !error && sortedResults.length > 0 && (
          <TableContainer>
            <Table size="small" aria-label="Fulfillment models leaderboard">
              <TableHead>
                <TableRow>
                  <TableCell width={56}>Rank</TableCell>
                  <TableCell>Analyst</TableCell>
                  <TableCell align="right" width={72}>
                    Total
                  </TableCell>
                  {showPreparer && (
                    <TableCell align="right" width={88}>
                      Preparer
                    </TableCell>
                  )}
                  {showReviewer && (
                    <TableCell align="right" width={88}>
                      Reviewer
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedResults.map((entry, index) => (
                  <TableRow
                    key={entry.user_id}
                    data-testid={`fulfillment-models-leaderboard-row-${entry.user_id}`}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{getLeaderboardDisplayName(entry)}</TableCell>
                    <TableCell align="right">{entry.total_model_count.toLocaleString()}</TableCell>
                    {showPreparer && (
                      <TableCell align="right">{entry.preparer_model_count.toLocaleString()}</TableCell>
                    )}
                    {showReviewer && (
                      <TableCell align="right">{entry.reviewer_model_count.toLocaleString()}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1, px: 2 }}>
        <Button
          size="small"
          sx={{ fontSize: '0.75rem' }}
          onClick={handleClose}
          data-testid="fulfillment-models-leaderboard-close"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
